import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import { NotificationManager, SiteConfigManager } from '@atlas/core';
import { DefaultPopup } from '@atlas/ui/build/common/Popup';

import { SIGN_UP_POPUP_STORAGE_KEY } from 'common/constants';
import useBonuses from 'common/Hooks/useBonuses';
import { displayMultiPopup } from 'common/Popup/MultiPopup/MultiPopupManager';
import { getTerminalLoginConfig } from 'common/Popup/TerminalLoginPopup/TerminalLoginPopupManager';
import useLanguage from 'src/hooks/useLanguage';

import BonusCardWrapper from './BonusCardWrapper';
import BonusesWrapper from './BonusesWrapper';

const RegistrationBonusPopup = ({ id, onClose = () => {} }) => {
  const { activateBonus, bonuses = [], isLoading, wallet } = useBonuses();
  const { push: historyPush } = useHistory();
  const [isOpenedDetails, setIsOpenedDetails] = useState(false);
  const leaveAfterFirstStep = SiteConfigManager.getConfig(
    'config.config.registration.close_multistep_after_first_step',
    false,
  );
  const { alpha3Code } = useLanguage();

  const filteredBonuses = useMemo(
    () =>
      bonuses
        .map(bonus => {
          const bonusImage = bonus?.localizedImageInfoList?.find(
            item => item.language.toLowerCase() === alpha3Code.toLowerCase(),
          );

          return {
            ...bonus,
            imageDescription: bonusImage?.imageDescription,
            imageLink: bonusImage?.imageLink,
          };
        })
        .filter(bonus => bonus.imageDescription || bonus.imageLink),
    [bonuses, alpha3Code],
  );

  const areBonusesEmpty = filteredBonuses.length === 0;

  const closePopupHandler = useCallback(() => {
    if (leaveAfterFirstStep) {
      historyPush(window.location.pathname);
    }
    NotificationManager.removeNotification(id);

    const terminalLoginPopupConfig = getTerminalLoginConfig();
    if (terminalLoginPopupConfig) {
      NotificationManager.display({
        ...terminalLoginPopupConfig,
        data: {
          ...terminalLoginPopupConfig.data,
          onClose: () => displayMultiPopup('sign_up', SIGN_UP_POPUP_STORAGE_KEY),
        },
      });
    } else {
      displayMultiPopup('sign_up', SIGN_UP_POPUP_STORAGE_KEY);
    }

    onClose();
  }, [historyPush, id, onClose]);

  const activateBonusHandler = useCallback(
    async bonus => {
      closePopupHandler();
      await activateBonus(bonus);
    },
    [activateBonus, closePopupHandler],
  );

  useEffect(() => {
    if (!isLoading && areBonusesEmpty) {
      closePopupHandler();
    }
  }, [isLoading, closePopupHandler, areBonusesEmpty]);

  return (
    !isLoading &&
    !areBonusesEmpty && (
      <DefaultPopup
        className="registration-bonus-popup"
        headerTitle="registration_bonus_popup_header"
        onClose={closePopupHandler}
      >
        <BonusesWrapper
          bonuses={filteredBonuses}
          isOpenedDetails={isOpenedDetails}
          renderBonus={bonus => (
            <BonusCardWrapper
              activateBonus={activateBonusHandler}
              bonus={bonus}
              currency={wallet.currency}
              key={bonus.name}
              setIsOpenedDetails={setIsOpenedDetails}
            />
          )}
        />
      </DefaultPopup>
    )
  );
};

RegistrationBonusPopup.propTypes = {
  id: PropTypes.number,
  onClose: PropTypes.func,
};

export default RegistrationBonusPopup;
